import { Button, Flex, Result, Spin } from "antd"
import { useTranslation } from "react-i18next"
import { useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Text } from "ui/Typography"
import { spacings } from "themes/variables"
import { numberToCurrency } from "helpers/string"
import {
  clearAttemptRef,
  getPowerTranzErrorMessage,
  getSavedAttemptRef,
  getSavedConfirmationPath,
  isConfirmedAttemptState,
  isFailedAttemptState
} from "helpers/powertranz"
import { ATTEMPT_STATES, ERROR_CODES, HOSTED_PAGE_STATES } from "constants/powertranz"
import { usePowerTranzStatusPolling } from "hooks/usePowerTranzStatusPolling"
import styles from "./index.module.css"

// The attempt reference is precise and needs no sign-in; the URL drops it when the callback
// couldn't be matched (reason=unknown_token), so fall back to the one saved at checkout, then to
// the booking's latest attempt (signed-in users only). Nothing at all = nothing to look up.
const buildStatusParams = (attemptRef, bookingId) => {
  const ref = attemptRef || getSavedAttemptRef(bookingId)
  if (ref) return { attempt_ref: ref }
  if (bookingId) return { booking_id: bookingId }
  return null
}

// Guests can't act on the guide's own setup problems; they see one message for them (guide 5.6).
const GUEST_SETUP_ERROR_CODES = [ERROR_CODES.INVALID_CREDENTIALS, ERROR_CODES.HOSTED_PAGE_NOT_FOUND]
const guestFailureCode = (code) => (GUEST_SETUP_ERROR_CODES.includes(code) ? ERROR_CODES.MERCHANT_UNAVAILABLE : code)

// Result page for a PowerTranz checkout (spec "Frontend contract" 2–3). Reached at
// /payment-successful or /payment-failure with provider=powertranz; whichever URL it is, the
// outcome shown is always the one GET /powertranz/status reports.
const PowerTranzPaymentResult = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const attemptRef = searchParams.get("attempt_ref")
  const bookingId = searchParams.get("booking_id")
  const reasonFromUrl = searchParams.get("reason")
  const statusParams = buildStatusParams(attemptRef, bookingId)
  const { status, isRequestFailed, isNotFound, isTimedOut } = usePowerTranzStatusPolling(statusParams)
  const isConfirmed = isConfirmedAttemptState(status?.state)
  const isFailed = isFailedAttemptState(status?.state)

  // The outcome is final: the saved attempt is no longer needed for recovery. A confirmed payment
  // moves on to the booking confirmation page checkout saved (as Stripe and PayPal do); without one
  // (another browser, an emailed link) this page's own success message stays.
  const statusBookingId = bookingId || status?.booking?.id
  useEffect(() => {
    if (!isConfirmed && !isFailed) return
    const confirmationPath = isConfirmed ? getSavedConfirmationPath(statusBookingId) : null
    clearAttemptRef(statusBookingId)
    if (confirmationPath) navigate(confirmationPath, { replace: true })
  }, [isConfirmed, isFailed, statusBookingId, navigate])

  if (isConfirmed) {
    return (
      <Flex className={styles.root} justify="center" align="center">
        <Result
          status="success"
          title={t("payment.success_title")}
          subTitle={
            <Flex vertical gap={spacings.XXS}>
              <Text>{t("payment.success_subtext")}</Text>
              {status.income?.amount != null && (
                <Text strong>{t("powertranz.result.amount_paid", { amount: numberToCurrency(status.income.amount) })}</Text>
              )}
              {status.savedCard?.lastFour && (
                <Text>{t("powertranz.result.card_saved", { brand: status.savedCard.brand, lastFour: status.savedCard.lastFour })}</Text>
              )}
            </Flex>
          }
        />
      </Flex>
    )
  }

  if (!statusParams || isFailed) {
    return (
      <Flex className={styles.root} justify="center" align="center">
        <Result
          status="error"
          title={t("payment.failure_title")}
          subTitle={getPowerTranzErrorMessage(t, guestFailureCode(status?.failureReason || reasonFromUrl))}
          extra={
            <Button type="primary" onClick={() => navigate(-1)}>
              {status?.state === ATTEMPT_STATES.EXPIRED ? t("powertranz.checkout.start_again") : t("powertranz.checkout.try_again")}
            </Button>
          }
        />
      </Flex>
    )
  }

  if (isNotFound) {
    return (
      <Flex className={styles.root} justify="center" align="center">
        <Result status="info" title={t("powertranz.result.not_found_title")} subTitle={t("powertranz.result.not_found_text")} />
      </Flex>
    )
  }

  if (isTimedOut) {
    return (
      <Flex className={styles.root} justify="center" align="center">
        <Result status="info" title={t("powertranz.result.still_confirming_title")} subTitle={t("powertranz.result.still_confirming_text")} />
      </Flex>
    )
  }

  // The guest hasn't finished the hosted page yet (another tab, or the popup is still open).
  const isOnHostedPage = HOSTED_PAGE_STATES.includes(status?.state)

  // Still settling (or the first status call hasn't answered): wait, never offer a retry — the
  // charge may already have gone through.
  return (
    <Flex className={styles.root} justify="center" align="center">
      <Result
        icon={<Spin size="large" />}
        title={isOnHostedPage ? t("powertranz.result.waiting_title") : t("powertranz.result.confirming_title")}
        subTitle={isRequestFailed ? t("powertranz.result.status_unreachable") : t("powertranz.result.confirming_text")}
      />
    </Flex>
  )
}

export default PowerTranzPaymentResult
